import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Input, Switch } from "@mui/material";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import instance from "../../redux/api";
import { numberFormatter } from "../../utils/generalFunctions";
import { CustomLoader } from "../CustomLoader";
import { commonToast } from "../common";
import { CommonButton } from "../common/commonButton";
import DialogBox from "../common/dialog";
import AddNewAllocation from "./AddNewAllocation";
import AllocationsTable from "./AllocationsTable";
import CashAllocationEdit from "./CashAllocationEdit";
import CorrectionTypes from "./CorrectionTypes";
import CorrectiveTransfer from "./CorrectiveTransfer";
import FileUpload from "./FileUpload";
import WriteOffConfirmation from "./WriteOffConfirmation";
import "./cash-allocation.scss";
import {
  useAllocationList,
  useBankDetails,
  useCreateContraWorkflow,
  useSearchParams,
} from "./hooks";
import { parseDate } from "./utils";
import { PolicySharp } from "@mui/icons-material";
import { ROEFILTERAPI } from "../../Services/api";
import { BankTransaction } from "../../Services/api";

export default function DataTable() {
  const cashAllocationDetailsClose = "action-button filled pill danger";

  const { userData } = useSelector((state) => state?.user);

  const transactionId = useSearchParams("txn_id");
  const policyRef = useSearchParams("policy_ref");
  const [policyRefNo, setPolicyRefNo] = useState(policyRef);
  const [selectedRows, setSelectedRows] = useState([]);
  const [addingRecord, setAddingRecord] = useState(false);
  const [validationStatus, setValidationStatus] = useState([]);
  const [isEditingRecord, setIsEditingRecord] = useState(null);
  const { bankDetails, transactionDetails, isBankDetailsApiInProgress } =
    useBankDetails(transactionId);
  // const [selectedRowIndex, setSelectedRowIndex] = useState(-1);
  const [isAPIInProgress, setIsAPIInProgress] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(true);
  const [openDeleteConfirmDialog, setOpenDeleteConfirmDialog] = useState(false);
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);

  const [triggerCTWorkflow, setTriggerCTWorkflow] = useState(false);
  const [triggerCorrectionTypesWorkflow, setTriggerCorrectionTypesWorkflow] =
    useState(false);
  const [currentAllocation, setCurrentAllocation] = useState(null);
  const [cashAllocationDetailsEditOpen, setCashAllocationDetailsEditOpen] =
    useState(false);
  const [updatedFields, setUpdatedFields] = useState({});
  const [writeOffConfirmationOpen, setWriteOffConfirmationOpen] = useState(false);

  // Contra Allocation States
  const [associatedAllocationActivities, setAssociatedAllocationActivities] =
    useState({});
  const [
    openContraAllocationConfirmDialog,
    setOpenContraAllocationConfirmDialog,
  ] = useState(false);
  const [contraAllocationDialogBody, setContraAllocationDialogBody] =
    useState("");
  const [contraComment, setContraComment] = useState("");
  const [contraReferencePolicies, setContraReferencePolicies] = useState([]);
  const [
    contraReferencePoliciesInputsValues,
    setContraReferencePoliciesInputsValues,
  ] = useState({});

  const [showContraAddNewAllocation, setShowContraAddNewAllocation] =
    useState(false);
  // Global Loader
  const [isLoading, setIsLoading] = useState(false);
  
  const [isAuthorizedUser, setIsAuthorizedUser] = useState(false);

  const [openDialogueId, setOpenDialogueId] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuId, setMenuId] = useState(null);
  const [dialogueAllocation, setDialogueAllocation] = useState(null);

  // hooks
  const {
    allocationList,
    setAllocationList,
    allocationsWithInitialStatus,
    saveAllocationData,
    deleteAllocationData,
    totalAllocatedAmount,
    totalReceivableAmount,
    // totalWriteOffAmount,
    loadData,
    apiProgressBar,
    setApiProgressBar,
    selectedRowIndex,
    setSelectedRowIndex,
    isEditPolicy,
    setIsEditPolicy,
  } = useAllocationList(transactionId);

  useEffect(() => {
    if (
      (userData?.id &&
        transactionDetails?.Assigned_User?.id &&
        userData?.id === transactionDetails?.Assigned_User?.id) ||
      !transactionDetails?.Assigned_User ||
      userData?.role?.toLowerCase() === "manager"
    ) {
      setIsAuthorizedUser(true);
    } else {
      setIsAuthorizedUser(false);
    }
  }, [userData, transactionDetails, allocationList]);

  const handleDeleteClick = async (id) => {
    try {
      setIsDeleteInProgress(true);
      const response = await deleteAllocationData(id);

      // close delete confirm dialog
      setOpenDeleteConfirmDialog(false);

      // Check if the deletion was successful
      if (response.status === 204 || response.status === 200) {
        // If successful, show a success toast
        commonToast("success", "Item deleted successfully");
        // If needed, update the state to reflect the deletion
        const updatedAllocations = allocationList.filter(
          (row) => row.id !== id
        );
        setAllocationList([...updatedAllocations]);
        setSelectedRows([]);
        setSelectedRowIndex(-1);
        setIsDeleteInProgress(false);
        if (allocationList.length > 0) {
          setPolicyRefNo(allocationList[0].id);
        }
      } else {
        // If deletion was not successful, show an error toast
        commonToast("error", `Error deleting item: ${response.statusText}`);
        setIsDeleteInProgress(false);
      }
    } catch (error) {
      // If an error occurs during deletion, show an error toast
      commonToast("error", `Error deleting item: ${error.message}`);
      setIsDeleteInProgress(false);
    }
  };

  const displayToastMessage = (message, type = "error") => {
    toast.error(message, {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 5000,
    });
  };

  // Trigger Auto Initiation of Corrective Transfer
  async function isAutoCorrectiveTransferTriggerd(allocationDetails) {
    try {
      let correctiveTransfers = null;
      try {
        // API Call to get corrective transfer details for allocation
        correctiveTransfers = await instance.get(
          `bankmanagement/get_corrective_by_allocation_id?cashallocation_id=${allocationDetails?.id}`
        );
      } catch (error) {
        if (error.status != 404) {
          commonToast("error", "Auto Intiation of corrective transfer failed.");

          return false;
        }
      }

      // Check if corrective Transfer already not exists for this allocation
      // Check if Producing Entity exists and not equal to receiving entity
      // Check if receivable amount is greater than 0
      if (
        allocationDetails?.allocation_status === "Allocated" &&
        (!correctiveTransfers ||
          !correctiveTransfers?.data ||
          correctiveTransfers?.data.length === 0) &&
        allocationDetails?.policy_fk?.Producing_Entity &&
        allocationDetails.policy_fk.Producing_Entity !==
        allocationDetails?.bank_txn?.bank_details?.entity_number &&
        allocationDetails?.receivable_amt != 0
      ) {
        setTriggerCTWorkflow(true);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      commonToast("error", "Auto Intiation of corrective transfer failed.");

      return false;
    }
  }

  function isAutoCorrectionTypesTriggered(allocationDetails) {
    try {
      const old_allocation_status =
        allocationDetails?.audit_fields?.fields?.filter(
          (field) => field.field_name === "allocation_status"
        )[0].old_value ?? "";
      if (
        allocationDetails?.allocation_status.startsWith("Query") &&
        allocationDetails?.allocation_status !== old_allocation_status
      ) {
        setTriggerCorrectionTypesWorkflow(true);
        return true;
      } else {
        return false;
      }
    } catch (error) {
      commonToast("error", "Auto Intiation of correction reason failed.");
      return false;
    }
  }

  // const [pendingWriteOffAllocation, setPendingWriteOffAllocation] = useState(null);
  // const hasTriggeredRef = useRef(false);

  // const WriteOffAutoTrigger = ({ allocation }) => {
  //   const { submit } = useWriteOff(allocation, null, {
  //     request_date: getTodaysDateInLocalTimezone(),
  //     aprover_name: "",
  //     writeoff_reason: "Auto round off",
  //     writeoff_amt: parseFloat(
  //       (transactionDetails.Receivable_Amount - totalAllocatedAmount).toFixed(2)
  //     ),
  //     currency: allocation?.bank_txn?.Bank_Currency_Code || "",
  //     comments: "System generated and approved WF as var is < 1",
  //     bank_txn: allocation?.bank_txn?.id,
  //     cash_allocation: allocation?.id,
  //   });

  //   useEffect(() => {
  //     if (!allocation?.id) return;
  //     // Prevent duplicate execution (StrictMode + re-renders)
  //     if (hasTriggeredRef.current) return;
  //     hasTriggeredRef.current = true;

  //     const triggerWriteOff = async () => {
  //       try {
  //         console.log("allocationDetails", allocation);

  //         // allow React to apply state before submit
  //         await Promise.resolve();

  //         const submitResponse = await submit();
  //         console.log("Write-off auto-initiated:", submitResponse);

  //         setPendingWriteOffAllocation(null);

  //       } catch (error) {
  //         console.error("Auto write-off initiation failed:", error);
  //         commonToast("error", "Auto initiation of write-off failed.");
  //         setPendingWriteOffAllocation(null);
  //       }
  //     };

  //     triggerWriteOff();

  //   }, [allocation, submit]);

  //   return null;
  // };
  // Write-off auto-trigger function at component level
  // const triggerWriteOffAuto = useCallback(async (allocation) => {
  //   console.log("allocationDetails", allocation);
  //   try {
  //     // Always trigger write-off without conditions
  //     setPendingWriteOffAllocation(allocation);
  //     return true;
  //   } catch (error) {
  //     console.error("Auto write-off initiation failed:", error);
  //     commonToast("error", "Auto initiation of write-off failed.");
  //     return false;
  //   }
  // }, []);

  const handleSubmit = async ({ policy, ...item }) => {
    try {
      // Bank roe should be 1 when Bank_Currency_Code and original_ccy is same
      if (item?.bank_txn?.Bank_Currency_Code === item?.original_ccy) {
        item.bank_roe = 1.0;
      }
      if (Number(item?.bank_roe) <= 0) {
        displayToastMessage("Bank ROE cannot be Zero");
        return;
      }

      // Add validation for SCM and Non-SCM binding agreement
      if (["SCM", "Non-SCM"].includes(item?.binding_agreement)) {
        const missingFields = [];
        if (!item?.XFIbatchno || item?.XFIbatchno === "0")
          missingFields.push("Batch");
        if (!item?.cash_reference) missingFields.push("Cash Reference");

        if (missingFields.length > 0) {
          displayToastMessage(
            `${missingFields.join(", ")} ${missingFields.length === 1 ? "is" : "are"
            } mandatory for ${item.binding_agreement} binding agreement`
          );
          return;
        }
      }

      // Trigger UI Notification when allocation_status is Allocated and Receivable, Allocated amounts are Zero
      if (item?.allocation_status === "Allocated") {
        if (Number(item?.receivable_amt) === 0) {
          displayToastMessage("Receivable amount cannot be Zero");
          return;
        }
        if (Number(item?.allocated_amt) === 0) {
          displayToastMessage("Allocated amount cannot be Zero");
          return;
        }
        if (
          parseFloat(item?.receivable_amt) != parseFloat(item?.allocated_amt)
        ) {
          displayToastMessage("Remaining Balance should be Zero");
          return;
        }
        // variance check
        if (parseFloat(transactionDetails.Receivable_Amount - totalAllocatedAmount) < 1 && parseFloat(transactionDetails.Receivable_Amount - totalAllocatedAmount) !== 0) {
          setWriteOffConfirmationOpen(true);
          return;
        }
        
        // Added by Gaurav on 28-11-2024
        item.allocatedby = userData.user_name;
        item.allocation_date = parseDate(new Date());
      }

      setCurrentAllocation(item);

      // Update audit fields
      const { audit_fields: auditFields } = item;
      auditFields.fields[0].value = item?.policy_id;
      auditFields.fields[1].value = item?.allocation_status;
      auditFields.fields[2].value = item?.receivable_amt;
      auditFields.fields[3].value = item?.allocated_amt;
      auditFields.user_id = userData?.id;

      const payload = {
        ...item,
        bank_txn: item?.bank_txn?.id ?? 0,
        policy_fk: item?.policy_fk?.id ?? null,
        policy_pk: item?.policy_pk ?? item?.policy_fk?.id,
        policy_mf: item?.policy_mf ?? [],
        audit_fields: auditFields,
        updatedFields: updatedFields[selectedRowIndex] || {},
      };
      setIsAPIInProgress(true);
      const response = await saveAllocationData(payload);
      setIsAPIInProgress(false);

      if (response.status === 200 || response.status === 201) {
        let shouldReload = true;

        // Auto Initiation of Corrective transfer check
        const isAutoInitiationTriggered =
          await isAutoCorrectiveTransferTriggerd(item);
        if (isAutoInitiationTriggered) {
          shouldReload = false;
        }

        // Auto Initiation of Correction Type check
        const isCorrectionTypesAutoInitiationTriggered =
          isAutoCorrectionTypesTriggered(item);

        if (isCorrectionTypesAutoInitiationTriggered) {
          shouldReload = false;
        }

        commonToast("success", "Item Updated successfully!");

        if (shouldReload) {
          await loadData();
          // Clear the updated fields for this row after save
          setUpdatedFields((prev) => {
            const newState = { ...prev };
            delete newState[selectedRowIndex];
            return newState;
          });
        }
      }
    } catch (err) {
      console.error("Error:", err);
      setIsAPIInProgress(false);
      commonToast(
        "error",
        err?.data?.msg || "Unable to save cash allocation data"
      );
    }
  };

  const startAddingNewRow = () => {
    if (isAuthorizedUser) {
      setAddingRecord(true);
    } else {
      toast.error(
        "You are not assigned user!! Please re-assign and then try.",
        {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 1000,
        }
      );
    }
  };

  const [isAddRow, setIsAddRow] = useState(false);

  const handleAddRow = (data) => {
    commonToast("success", "Successfully Added new allocation!");
    setPolicyRefNo(data?.id);
    setIsAddRow(true);

    if (data?.bank_txn) {
      loadData(data?.policy_id);
    } else {
      try {
        instance
          .get(
            `bankmanagement/get_transactions_by_txn_id/?txn_id=${encodeURIComponent(
              data?.bank_txn
            )}`
          )
          .then((response) => {
            let data = response.data;
            // disable the row then when the allocation status is "Allocated"
            data = data?.map((item) => {
              return {
                ...item,
                disabled: item?.allocation_status === "Allocated",
                audit_fields: {
                  user_id: userData?.id,
                  fields: [
                    {
                      field_name: "policy_id",
                      old_value: item?.policy_id,
                      value: item?.policy_id,
                    },
                    {
                      field_name: "allocation_status",
                      old_value: item?.allocation_status,
                      value: item?.allocation_status,
                    },
                    {
                      field_name: "receivable_amt",
                      old_value: item?.receivable_amt,
                      value: item?.receivable_amt,
                    },
                    {
                      field_name: "allocated_amt",
                      old_value: item?.allocated_amt,
                      value: item?.allocated_amt,
                    },
                  ],
                  txn_id: item?.audit_txn?.id,
                  audit_txn_name: "CASH_ALLOCATION",
                },
              };
            });
            setAllocationList([...data]);
          })
          .catch((error) => {
            console.error("Error fetching allocations:", error);
            commonToast("error", "Unable fetch the added new allocation!");
          });
      } catch (err) {
        console.error("err", err);
      }
    }

    cancelAddRow();
  };

  const cancelAddRow = () => {
    setAddingRecord(false);
    setIsEditingRecord(null);
  };

  // Add state for modal visibility
  const [isPolicyErrorModalOpen, setIsPolicyErrorModalOpen] = useState(false);

  // Function to handle modal close
  const handleClosePolicyErrorModal = () => {
    setIsPolicyErrorModalOpen(false);
  };

  const handleDeleteRow = async () => {
    if (selectedRowIndex === -1) {
      commonToast("error", `Please select one row to delete`);
      return;
    }

    if (allocationList[selectedRowIndex]?.is_contra_allocation) {
      commonToast("error", "Contra allocation cannot be deleted.");

      return;
    }

    if (allocationList[selectedRowIndex]?.child_contra_id > 0) {
      commonToast(
        "error",
        "Can't delete allocation which has contra allocation"
      );

      return;
    }

    try {
      const response = await instance.get(
        `bankmanagement/cash_allocation_activities/${allocationList[selectedRowIndex].id}/`
      );
      if (response.status === 200 && response.data.message === "Success") {
        setOpenDeleteConfirmDialog(true);
      } else {
        //setErrorText(response.data.data)
        setOpenDeleteConfirmDialog(true);
        // setIsPolicyErrorModalOpen(true);
      }
    } catch (error) {
      commonToast("error", "Error while getting details of the policy");
    }
  };

  const handleEditRow = () => {
    if (selectedRowIndex === -1) {
      commonToast("error", "Error while getting details of the policy");
      return;
    }

    if (allocationList[selectedRowIndex]?.is_contra_allocation) {
      commonToast("error", "Contra allocation cannot be edited.");
      return;
    }

    if (allocationList[selectedRowIndex]?.child_contra_id > 0) {
      commonToast("error", "Can't edit allocation which has contra allocation");
      return;
    }

    setCashAllocationDetailsEditOpen(true);
  };

  const toggleTransactionDetails = useCallback(() => {
    setShowTransactionDetails((t) => !t);
  }, [setShowTransactionDetails]);

  const handleDeleteCancelled = () => {
    setOpenDeleteConfirmDialog(false);
  };

  const handleDeleteConfirmed = () => {
    const row = allocationList[selectedRowIndex];
    handleDeleteClick(row.id);
  };

  async function handleContraAllocationClick() {
    try {
      setIsLoading(true);
      let contraRow = allocationList[selectedRowIndex];
      const allocationId = contraRow.id;

      if (contraRow?.is_contra_allocation) {
        toast.error(
          "Contra allocation cannot be added to an existing contra allocation entry.",
          {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 1000,
          }
        );

        setIsLoading(false);
        return;
      }

      if (contraRow?.child_contra_id > 0) {
        toast.error(
          "Multiple contra allocations cannot be added to the same entry.",
          {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 1000,
          }
        );

        setIsLoading(false);
        return;
      }

      // Handle Workflows on Contra Allocation
      let associatedActivities = await getAssociatedAllocationActivities(
        allocationId
      );
      setAssociatedAllocationActivities((prevState) => ({
        ...prevState,
        [allocationId]: associatedActivities,
      }));

      setIsLoading(false);
      setOpenContraAllocationConfirmDialog(true);
    } catch (error) {
      setIsLoading(false);

      // If an error occurs during contra creation, show an error toast
      toast.error(`Error creating Contra Allocation`, {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 1000,
      });
    }
  }

  async function getAssociatedAllocationActivities(allocationId) {
    try {
      // retrieve workflows list for the allocation
      const response = await instance.get(
        `bankmanagement/cash_allocation_activities/${allocationId}/`
      );
      if (response.status === 200) {
        let dialogueBody = (
          <>
            <b>Are you sure you want to create a Contra Allocation ?</b>
          </>
        );
        let associatedActivities = null;

        // success here defines no activities associated
        if (response.data.message != "Success") {
          associatedActivities = response.data.data;

          // Adjusting Dialoge
          const activityNameList = Object.keys(associatedActivities).map(
            (key, index) => (
              <span key={index}>
                {key}
                <br />
              </span>
            )
          );
          dialogueBody = (
            <>
              The following activities are associated with the selected
              allocation : <br />
              {activityNameList}
              <br />
              <b>Are you sure you want to create a Contra Allocation?</b>
            </>
          );
        }

        setContraAllocationDialogBody(dialogueBody);
        return associatedActivities;
      } else {
        const errorMessage =
          "Error While Retrieving Associated Allocation Activities";
        console.error(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Error:", error);
      throw error;
    }
  }

  const CustomContraBody = () => {
    return (
      <div
        style={{
          width: "90%",
          maxWidth: "90%", // Prevent table from overflowing
          borderCollapse: "collapse",
          margin: "0 0",
          fontSize: "16px",
          textAlign: "left",
          // overflowX: "hidden",
        }}
      >
        {contraAllocationDialogBody && (
          <span>{contraAllocationDialogBody}</span>
        )}

        <div style={{ marginTop: "10px", width: "100%" }}>
          <div style={{ marginBottom: "10px" }}>
            <CommonButton
              text="Add Policy"
              handleClick={() => setShowContraAddNewAllocation(true)}
              sx={{ borderRadius: "30px" }}
              hoverColor="#FF5A01"
            />
          </div>
          {/* Show the table of added reference policies directly */}
          <div style={{ marginTop: "20px" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                margin: "20px 0",
                fontSize: "16px",
                textAlign: "left",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#f4f4f4" }}>
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    Policy
                  </th>
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    Receivable Amount <br />
                    <span style={{ fontWeight: "normal" }}>
                      [Total Amount To Allocate:{" "}
                      {allocationList &&
                        allocationList[selectedRowIndex] &&
                        allocationList[selectedRowIndex].receivable_amt
                        ? allocationList[selectedRowIndex].receivable_amt
                        : 0}
                      ]
                    </span>
                  </th>
                  <th
                    style={{
                      border: "1px solid #ddd",
                      padding: "12px",
                      fontWeight: "bold",
                    }}
                  >
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {contraReferencePolicies.length > 0 ? (
                  contraReferencePolicies.map((policyData, index) => {
                    const policyIdentifier =
                      policyData.policyIdentifier || policyData.Policy_Line_Ref;
                    return (
                      <tr key={policyIdentifier}>
                        <td>{policyIdentifier}</td>
                        <td style={{ textAlign: "center" }}>
                          <div
                            style={{
                              position: "relative",
                              width: "120px",
                              margin: "0 auto",
                              padding: "8px",
                            }}
                          >
                            <Input
                              type="number"
                              value={
                                contraReferencePoliciesInputsValues[
                                policyIdentifier
                                ] || ""
                              }
                              onChange={(e) =>
                                setContraReferencePoliciesInputsValues(
                                  (prev) => ({
                                    ...prev,
                                    [policyIdentifier]: e.target.value,
                                  })
                                )
                              }
                              disableUnderline
                              style={{
                                textAlign: "center",
                                width: "100%",
                                background: "transparent",
                                zIndex: 1,
                                position: "relative",
                              }}
                            />
                            <div
                              style={{
                                position: "absolute",
                                left: 0,
                                right: 0,
                                top: "65%",
                                borderBottom: "2px solid #888",
                                zIndex: 0,
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <CommonButton
                            text="Remove"
                            handleClick={() =>
                              removeContraPolicy(policyIdentifier)
                            }
                            sx={{ borderRadius: "20px", fontSize: "12px" }}
                            hoverColor="#FF5A01"
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="3"
                      style={{
                        textAlign: "center",
                        padding: "20px",
                        color: "#666",
                      }}
                    >
                      No policies added yet. Click "Add Policy" to add reference
                      policies.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div>
          <textarea
            style={{
              width: "300px",
              height: "100px",
              marginTop: "20px",
            }}
            placeholder="Enter your comments..."
            value={contraComment}
            onChange={(e) => setContraComment(e.target.value)}
          />
        </div>
      </div>
    );
  };

  async function createContraManualPolicies(policyData) {
    /**
     * Create a manual contra policy by sending payload to the API.
     */
    try {
      const payload = {
        Binding_Agreement: policyData.Binding_Agreement,
        Installment_Agency_Amount_in_Orig: policyData.Installment_Agency_Amount_in_Orig || null,
        Installment_Due_date: policyData.Installment_Due_date || null,
        Instalment_Nbr: policyData.Instalment_Nbr || null,
        Policy_Line_Ref: policyData.Policy_Line_Ref,
        Producing_Entity: policyData.Producing_Entity,
        SCM_Insurer_partner_name: policyData.SCM_Insurer_partner_name?.trim() || null,
        SCM_Partner: policyData.SCM_Partner?.trim() || null,
        Syndicate_Binder: policyData.Syndicate_Binder,
        Third_Party_Capacity: policyData.Third_Party_Capacity,
        Three_Party_Capacity_Deployed: policyData.Binding_Agreement === "SCM" ? "Yes" : "No",
        UMR_Number: policyData.UMR_Number,
        allocation_invoice_verification: policyData.allocation_invoice_verification,
        installment_amount_org: policyData.installment_amount_org,
        original_ccy: policyData.original_ccy,
        settlement_currency: policyData.settlement_currency
      };

      debugger
      const res = await instance.post("documents/policy_create/", payload);
      return res.status === 200 ? res.data.id : null;
    } catch (err) {
      console.error("Contra Manual Policy creation failed:", err);
      throw err;
    }
  }

  async function createContraReferencePoliciesDetails() {
    /**
     * Create allocation details for referenced contra policies.
     */
    try {
      const allocationDetails = [];

      for (const policyData of contraReferencePolicies) {
        const policyNumber = policyData.Policy_Line_Ref;
        const policy_receivable_amount = Number(contraReferencePoliciesInputsValues?.[policyNumber]) || 0;

        let policyId = policyData.id;
        if (policyData.isManual) {
          const manualPolicyId = await createContraManualPolicies(policyData);
          if (manualPolicyId) {
            policyId = manualPolicyId;
          }
        }

        allocationDetails.push({
          GXPbatchno: null,
          Third_Party_Capacity: policyData?.Three_Party_Capacity_Deployed ?? null,
          XFIbatchno: 0,
          accounting_monthyear: transactionDetails?.Accounting_Month
            ? format(new Date(transactionDetails.Accounting_Month), "yyyy-MM-dd")
            : null,
          allocated_amt: 0,
          allocatedby: null,
          allocation_binder: policyData.Syndicate_Binder || "-",
          allocation_date: null,
          allocation_eea: policyData.allocation_eea ?? null,
          allocation_entity: policyData.Producing_Entity || "-",
          allocation_invoice_verification: policyData.allocation_invoice_verification,
          allocation_scm: policyData.Binding_Agreement === "SCM" ? policyData.SCM_Partner ?? null : null,
          allocation_scmname: policyData.Binding_Agreement === "SCM" ? policyData.SCM_Insurer_partner_name || null : null,
          allocation_status: "In Progress - CC",
          allocation_systemid: "0",
          allocation_umr: policyData.UMR_Number || "-",
          audit_fields: {
            user_id: userData?.id,
            fields: [
              { field_name: "policy_id", old_value: null, value: policyNumber },
              { field_name: "allocation_status", old_value: null, value: "In Progress - CC" },
              { field_name: "receivable_amt", old_value: null, value: null },
              { field_name: "allocated_amt", old_value: null, value: null },
            ],
            txn_id: null,
            audit_txn_name: "CASH_ALLOCATION",
          },
          bank_curr: transactionDetails?.bank_details?.currency || "-",
          bank_roe: transactionDetails?.bank_roe || 0,
          bank_txn: transactionDetails.id,
          binding_agreement: policyData.Binding_Agreement === "SCM" ? "SCM" : "NON SCM",
          cash_reference: null,
          installment_number: policyData?.Instalment_Nbr ?? null,
          installment_duedate: policyData?.Installment_Due_date ?? null,
          installment_amount_org: policyData.isManual ? policyData.installment_amount_org ?? null : policyData.Installment_Agency_Amount_in_Orig ?? null,
          isActive: true,
          manual_entry: true,
          original_ccy: policyData.isManual ? policyData.original_ccy ?? null : policyData.Original_Ccy ?? null,
          payment_currency: policyData.isManual ? policyData.original_ccy || transactionDetails?.payment_currency : policyData.Original_Ccy || transactionDetails?.Payment_Currency_Code,
          policy_id: policyNumber,
          policy_pk: policyId,
          receivable_amt: policy_receivable_amount,
          settlement_currency: policyData.isManual ? policyData.settlement_currency ?? null : policyData.Settlement_Ccy ?? null,
          unallocated_amt: 0,
        });
      }

      return allocationDetails;
    } catch (err) {
      console.error("Contra Reference Policy Details creation failed:", err);
      throw err;
    }
  }

  function prepareContraReversalRow(contraRow) {
    /**
     * Prepares contra row data for reversal (negated values and cleanup).
     */
    contraRow.policy_pk = contraRow?.policy_fk?.id;
    contraRow.bank_txn = contraRow?.bank_txn?.id;
    contraRow.audit_txn = contraRow?.audit_txn?.id;
    contraRow.locked_by = contraRow?.locked_by?.id;
    contraRow.settlement_ccy = contraRow?.settlement_ccy?.id;
    contraRow.comment = contraComment;

    if (!contraRow.GXPbatchno) contraRow.GXPbatchno = null;

    // Negate all monetary amounts for reversal
    ["unallocated_amt", "allocated_amt", "receivable_amt", "installment_amount_org"].forEach(field => {
      contraRow[field] = -contraRow[field];
    });

    contraRow.referenced_contra_allocations = null;
    contraRow.is_contra_allocation = true;
    contraRow.parent_contra_id = contraRow.id;

    // Remove unnecessary fields
    delete contraRow.id;
    delete contraRow.policy_fk;
    delete contraRow.created_by;
    delete contraRow.updated_by;

    // Adjust audit fields
    contraRow.audit_fields.fields = contraRow.audit_fields.fields.map((field) => {
      if (["receivable_amt", "allocated_amt"].includes(field.field_name)) {
        return {
          ...field,
          old_value: -field.old_value,
          value: -field.value,
        };
      }
      return field;
    });

    return contraRow;
  }

  const { triggerContraWorkflow } = useCreateContraWorkflow();
  async function createContraAllocation() {
    /**
     * Function to create a contra allocation workflow or post it directly based on validation.
     */
    setIsLoading(true);

    if (!contraComment?.length) {
      toast.error("Comments are mandatory when creating a contra allocation.", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
      });
      setIsLoading(false);
      return;
    }

    try {
      const policyDetails = contraReferencePolicies.map((policy) => ({
        policy_id: policy.Policy_Line_Ref,
        receivable_amount: parseFloat(contraReferencePoliciesInputsValues[policy.Policy_Line_Ref] || 0),
      }));

      const policy_cash_allocation_details = await createContraReferencePoliciesDetails();
      const contraRow = { ...allocationList[selectedRowIndex] };
      const contra_cash_allocation_details = prepareContraReversalRow(contraRow);

      const payload = {
        policy_cash_allocation_details,
        contra_cash_allocation_details,
        contraComment
      };

      const totalReferenceAmount = Object.values(contraReferencePoliciesInputsValues)
        .reduce((sum, val) => sum + parseFloat(val || 0), 0);

      // If mismatch, trigger approval workflow
      if (Math.abs(totalReferenceAmount) !== Math.abs(parseFloat(contraRow.receivable_amt))) {
        await triggerContraWorkflow({
          contra_allocation_payload: {
            bank_transaction_id: transactionId,
            policyDetails,
            ...payload
          },
          bank_txn_id: transactionDetails.id,
          comments: contraComment,
          initiated_user_id: userData?.id,
        });

        toast.success("Contra allocation sent for approval.", {
          position: toast.POSITION.TOP_RIGHT,
          autoClose: 2000,
        });

      } else {
        const response = await instance.post("bankmanagement/create_contra_allocation/", payload);

        if (response?.data) {
          toast.success("Successfully Created Contra Policy", {
            position: toast.POSITION.TOP_RIGHT,
            autoClose: 1000,
          });
        }
      }

    } catch (error) {
      toast.error("Failed to Create Contra Policy", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 1000,
      });
    } finally {
      setIsLoading(false);
      setOpenContraAllocationConfirmDialog(false);
      resetContraAllocationState();
      await loadData();
    }
  }

  // Not using this for now, We will use it in future if required
  // Hooks initialize for Contra Entries
  // const { createContraWriteOff } = useWriteOff(allocationList[selectedRowIndex]);
  // const { createContraCrossAllocation } = useCrossAllocation(allocationList[selectedRowIndex]);
  // const { createContraRefund } = useRefund(allocationList[selectedRowIndex]);
  // const { createContraTreasuryUpdate } = useTreasuryUpdate(allocationList[selectedRowIndex]);
  // const { createContraCfi } = useCfi(allocationList[selectedRowIndex]);
  // const { createContraMSD } = useMsd(allocationList[selectedRowIndex]);
  // const { createContraCorrectiveTransfer } = useCorrectiveTrf(allocationList[selectedRowIndex], transactionDetails);
  // const { createContraCorrectionType } = useAllocationIssues({allocation : allocationList[selectedRowIndex], transaction: transactionDetails});
  // async function handleAssociatedAllocationActivitiesOnContraAllocation(allocationId) {
  //   try {
  //     const associatedActivities = associatedAllocationActivities[allocationId];

  //     // Creating Contra Activities for the contra allocation
  //     for (let activity in associatedActivities) {
  //       for (let currentActivity of associatedActivities[activity]) {
  //         switch (activity.toLowerCase()) {
  //           case "write-off":
  //             await createContraWriteOff(currentActivity, allocationId);
  //           case "cross-allocation":
  //             await createContraCrossAllocation(currentActivity, allocationId);
  //           case "refund":
  //             await createContraRefund(currentActivity, allocationId);
  //           case "tresury-update":
  //             await createContraTreasuryUpdate(currentActivity, allocationId);
  //           case "cfi":
  //             await createContraCfi(currentActivity, allocationId);
  //           case "msd-update":
  //             await createContraMSD(currentActivity, allocationId);
  //           case "correction-types":
  //             await createContraCorrectionType(currentActivity, allocationId);
  //           case "corrective-transfer":
  //             await createContraCorrectiveTransfer(currentActivity, allocationId);
  //           default:
  //             throw new Error("Invalid Activity Type");
  //         }
  //       }
  //     }
  //   } catch (error) {
  //     toast.error("Failed to Create Contra Associated Policies", {
  //       position: toast.POSITION.TOP_RIGHT,
  //       autoClose: 1000,
  //     });
  //     console.error("Error Creating Contra Associated Policies: ", error);
  //   }
  // }

  const handleContraAddNewAllocationSuccess = (data) => {
    const policyKey = data.policyIdentifier || data.Policy_Line_Ref;

    const existingPolicy = contraReferencePolicies.find(
      (policy) =>
        (policy.policyIdentifier || policy.Policy_Line_Ref) === policyKey
    );

    if (existingPolicy) {
      toast.warning("This policy is already added to the contra allocation", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 3000,
      });
      setShowContraAddNewAllocation(false);
      return;
    }

    setContraReferencePolicies((prev) => [...prev, data]);
    // Use the policy identifier as the key for input values
    setContraReferencePoliciesInputsValues((prev) => ({
      ...prev,
      [policyKey]: "",
    }));
    setShowContraAddNewAllocation(false);
  };

  // Handle cancel from AddNewAllocation for contra allocation
  const handleContraAddNewAllocationCancel = () => {
    setShowContraAddNewAllocation(false);
    // Reset contra reference input values when closing modal
    setContraReferencePoliciesInputsValues({});
  };

  const removeContraPolicy = (policyIdentifier) => {
    setContraReferencePolicies((prev) =>
      prev.filter(
        (policy) =>
          (policy.policyIdentifier || policy.Policy_Line_Ref) !==
          policyIdentifier
      )
    );
    setContraReferencePoliciesInputsValues((prev) => {
      const newValues = { ...prev };
      delete newValues[policyIdentifier];
      return newValues;
    });
  };

  const resetContraAllocationState = () => {
    setContraReferencePolicies([]);
    setContraReferencePoliciesInputsValues({});
    setContraComment("");
  };

  const [ROEList, UpdateROEList] = useState([]);

  useEffect(() => {
    const fetchROEList = async () => {
      try {
        const response = await instance.get(ROEFILTERAPI);
        if (response.status === 200) {
          UpdateROEList(response?.data);
        } else {
          UpdateROEList([]);
        }
      } catch (error) {
        UpdateROEList([]);
        console.log("Failed to fetch ROE list", error);
      }
    };
    fetchROEList();
  }, []);
  const handleWriteOffClose = () => {
    setWriteOffConfirmationOpen(false);
    loadData();
  };

  const completeBankTxn = async (allocationID) => {
    try {
      const response = await instance.patch(
        BankTransaction + allocationID + "/",
        {
          id: allocationID,
          updated_by: userData?.user_name ?? "",
          transaction_status: "Completed",
        }
      );
      if (response && response.status >= 200 && response.status < 300) {
        commonToast("success", "Bank transaction completed!");
      } else {
        commonToast("error", "Failed to update Bank transaction status. Please try again.");
      }
    }
    catch (error) {
      commonToast("error", "Failed to update Bank transaction status. Please try again.");
    }
  }

  const proceedWithAllocation = async (item) => {
    console.log("proceed", item);
    try {
      // Create immutable copy of item
      const updatedItem = { ...item };
      
      if (updatedItem?.allocation_status === "Allocated") {
        updatedItem.allocatedby = userData?.user_name ?? "";
        updatedItem.allocation_date = parseDate(new Date());
      }

      setCurrentAllocation(updatedItem);

      // Update audit fields with bounds checking
      const { audit_fields: auditFields } = updatedItem;
      if (auditFields?.fields && auditFields.fields.length >= 4) {
        auditFields.fields[0].value = updatedItem?.policy_id;
        auditFields.fields[1].value = updatedItem?.allocation_status;
        auditFields.fields[2].value = updatedItem?.receivable_amt;
        auditFields.fields[3].value = updatedItem?.allocated_amt;
      auditFields.user_id = userData?.id;
      }

      const payload = {
        ...updatedItem,
        bank_txn: updatedItem?.bank_txn?.id ?? 0,
        policy_fk: updatedItem?.policy_fk?.id ?? null,
        policy_pk: updatedItem?.policy_pk ?? updatedItem?.policy_fk?.id,
        policy_mf: updatedItem?.policy_mf ?? [],
        audit_fields: auditFields,
        updatedFields: updatedFields[selectedRowIndex] || {}
      };
      setIsAPIInProgress(true);
      const response = await saveAllocationData(payload);
      setIsAPIInProgress(false);

      if (response.status === 200 || response.status === 201) {
        let shouldReload = true;
        
             // Auto Initiation of Write Off check
        // const isWriteOffAutoInitiationTriggered = await triggerWriteOffAuto(item);
        // if (isWriteOffAutoInitiationTriggered) {
        //   shouldReload = false;
        // }

        // Complete bank transaction with error handling
        if (updatedItem?.bank_txn?.id) {
          try {
            await completeBankTxn(updatedItem.bank_txn.id);
          } catch (bankTxnError) {
            console.error("Bank transaction completion failed:", bankTxnError);
            commonToast("error", "Bank transaction completion failed. Please check manually.");
            // Continue with other checks even if bank transaction fails
          }
        }

        // Auto Initiation of Corrective transfer check
        const isAutoInitiationTriggered = await isAutoCorrectiveTransferTriggerd(item);
        if (isAutoInitiationTriggered) {
          shouldReload = false;
        }

        // Auto Initiation of Correction Type check
        const isCorrectionTypesAutoInitiationTriggered = isAutoCorrectionTypesTriggered(item);

        if (isCorrectionTypesAutoInitiationTriggered) {
          shouldReload = false;
        }

        commonToast("success", "Item Updated successfully!");

        if (shouldReload) {
          await loadData();
          // Clear the updated fields for this row after save
          setUpdatedFields(prev => {
            const newState = { ...prev };
            delete newState[selectedRowIndex];
            return newState;
          });
        }
      }
    } catch (err) {
      console.error("Error:", err);
      setIsAPIInProgress(false);
      commonToast("error", err?.data?.msg || "Unable to save cash allocation data");
    }
  };

  const handleAdjustAmount = () => {
    try {
      setWriteOffConfirmationOpen(false);
      
      // Revert allocation status to original value from cash_tracker_data
      const updatedAllocations = [...allocationList];
      const selectedRow = updatedAllocations[selectedRowIndex];
      if (selectedRow?.cash_tracker_data?.Allocation_Status) {
        const originalStatus = selectedRow?.cash_tracker_data?.Allocation_Status;
        selectedRow.allocation_status = originalStatus;
        setAllocationList(updatedAllocations);
      }
    } catch (error) {
      console.error("Error in handleAdjustAmount:", error);
      commonToast("error", "Failed to adjust amount. Please try again.");
      }
  };

  const handleAddAllocation = async () => {
    setWriteOffConfirmationOpen(false);
    // Continue with allocation processing using existing function
    const item = allocationList[selectedRowIndex];
    await proceedWithAllocation(item);
  };

  return (
    <>
      {/* Page Loader */}
      {isLoading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.8)", // Semi-transparent background
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1400, // Ensure loader appears on top
          }}
        >
          <CircularProgress size={60} style={{ color: "rgb(255, 90, 1)" }} />
        </Box>
      )}

      <div
        className={`cash-allocation-page ${showTransactionDetails ? "with-details" : "without-details"
          }`}
      >
        <div className="top-details-section">
          <div className="header">
            <div className="page-title-container">
              <span className="page-title">Cash Allocation Details</span>
            </div>
            <div className="page-actions" style={{ display: "flex" }}>
              <div>
                {" "}
                <div style={{ marginRight: "10px" }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      className="cash-alloc-indicator historical"
                      style={{ marginRight: "10px" }}
                    />{" "}
                    Historical
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div
                      className="cash-alloc-indicator non-historical"
                      style={{ marginRight: "10px" }}
                    />{" "}
                    Non-Historical
                  </div>
                </div>
              </div>
              <CommonButton
                text="X Close Details"
                handleClick={() => window.history.back()}
                className={cashAllocationDetailsClose}
              />
            </div>
          </div>
          <div className="transaction-details">
            <div className="transaction-details-header"> 
              <span className="switch-container">
                <span className="page-title">View Bank Details</span>
                <Switch
                  inputProps={{ "aria-label": "Show Transaction Details" }}
                  onChange={toggleTransactionDetails}
                  checked={showTransactionDetails}
                />
                <span className="top-amount-details">
                  Total Bank Rcvd:{" "}
                  <span>
                    {numberFormatter(transactionDetails.Receivable_Amount)}
                  </span>
                </span>
                <span className="top-amount-details">
                  Policy Prem:{" "}
                  <span>
                    {numberFormatter(totalReceivableAmount)}
                  </span>
                </span>
                <span className="top-amount-details">
                  Alloc:{" "}
                  <span>{numberFormatter(totalAllocatedAmount)}</span>
                </span>
                {/* <span className="top-amount-details">
                  Write off:{" "}
                  <span>{numberFormatter(totalWriteOffAmount)}</span>
                </span> */}
                <span className="top-amount-details">
                  Bank Rcvd vs Prem Rcvd:{" "}
                  <span>
                    {numberFormatter(
                      transactionDetails.Receivable_Amount -
                      totalReceivableAmount
                    )}
                  </span>
                </span>
                <span className="top-amount-details">
                  Policy vs Alloc:{" "}
                  <span>
                    {numberFormatter(totalReceivableAmount - totalAllocatedAmount)}
                  </span>
                </span>
                <span className="top-amount-details">
                  Total Unalloc:{" "}
                  <span>
                    {numberFormatter(transactionDetails.Receivable_Amount - totalAllocatedAmount)}
                  </span>
                </span>
              </span>
              <FileUpload transaction={transactionDetails}/>
            </div>
            {showTransactionDetails && (
              <div className="transaction-details-card">
                {bankDetails.map(({ label, value }) => (
                  <span
                    key={label}
                    className={`transaction-item ${label === "Payment reference" ? "w-50" : ""
                      }`}
                  >
                    <span className="transaction-item-label">{label}</span>
                    <span className="transaction-item-value" title={value}>
                      : {value}
                    </span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="action-container">
            <h4 className="">Remittance List</h4>
            <div className="table-actions">
              {["manager", "cc processor"].includes(
                userData?.role?.toLowerCase()
              ) &&
                allocationList?.length > 0 &&
                allocationsWithInitialStatus[selectedRowIndex]
                  ?.initialStatus === "Allocated" &&
                !allocationsWithInitialStatus[selectedRowIndex]
                  ?.is_contra_allocation && (
                  <CommonButton
                    text="Create Contra Allocation"
                    handleClick={() => handleContraAllocationClick()}
                    sx={{ borderRadius: "30px", mr: 2 }}
                    hoverColor="#FF5A01"
                  />
                )}
              <CommonButton
                text="Add"
                handleClick={() => startAddingNewRow()}
                sx={{ borderRadius: "30px" }}
                hoverColor="#FF5A01"
                disabled={addingRecord || apiProgressBar || isAPIInProgress || isBankDetailsApiInProgress || transactionDetails?.has_bank_txn_refund === true}
              />
              <CommonButton
                text="Edit"
                handleClick={() => handleEditRow()}
                sx={{ borderRadius: 100 }}
                variant="outlined"
                disabled={
                  selectedRowIndex === -1 ||
                  userData?.role?.toLowerCase() !== "manager" ||
                  apiProgressBar ||
                  isAPIInProgress ||
                  isBankDetailsApiInProgress
                }
                hoverColor="#FF5A01"
              />
              {allocationList?.length > 0 && (
                <CommonButton
                  text="Delete"
                  handleClick={() => handleDeleteRow()}
                  sx={{ borderRadius: "30px", marginLeft: "20px" }}
                  hoverColor="#FF5A01"
                  disabled={userData?.role?.toLowerCase() !== "manager" || allocationList[selectedRowIndex]?.allocation_status === "Allocated" || apiProgressBar || isAPIInProgress || isBankDetailsApiInProgress}
                />
              )}
            </div>
          </div>
        </div>

        <div className="table-section">
          <AllocationsTable
            allocations={allocationList}
            setAllocations={setAllocationList}
            allocationsWithInitialStatus={allocationsWithInitialStatus}
            validationStatus={validationStatus}
            setValidationStatus={setValidationStatus}
            handleSave={handleSubmit}
            transaction={transactionDetails}
            selectedRowIndex={selectedRowIndex}
            setSelectedRowIndex={setSelectedRowIndex}
            apiProgressBar={apiProgressBar}
            setApiProgressBar={setApiProgressBar}
            setIsEditingRecord={setIsEditingRecord}
            policyRef={policyRef}
            updatedFields={updatedFields}
            setUpdatedFields={setUpdatedFields}
            isAuthorizedUser={isAuthorizedUser}
            openDialogueId={openDialogueId}
            setOpenDialogueId={setOpenDialogueId}
            anchorEl={anchorEl}
            setAnchorEl={setAnchorEl}
            menuId={menuId}
            setMenuId={setMenuId}
            dialogueAllocation={dialogueAllocation}
            setDialogueAllocation={setDialogueAllocation}
            policyRefNo={policyRefNo}
            setPolicyRefNo={setPolicyRefNo}
            isEditPolicy={isEditPolicy}
            setIsEditPolicy={setIsEditPolicy}
            isAddRow={isAddRow}
            setIsAddRow={setIsAddRow}
            ROEList={ROEList}
            UpdateROEList={UpdateROEList}
          />
        </div>

        {isAPIInProgress && <CustomLoader />}

        <ToastContainer />

        {(addingRecord || isEditingRecord) && (
          <AddNewAllocation
            onSuccess={handleAddRow}
            onCancel={cancelAddRow}
            isEditingRecord={isEditingRecord}
            transaction={transactionDetails}
          />
        )}

        {/* AddNewAllocation for Contra Allocation */}
        {showContraAddNewAllocation && (
          <AddNewAllocation
            onSuccess={handleContraAddNewAllocationSuccess}
            onCancel={handleContraAddNewAllocationCancel}
            transaction={transactionDetails}
            isContraMode={true}
          />
        )}

        <DialogBox
          title="Delete Confirmation"
          body={`Are you sure you want to delete this policy ${allocationList?.[selectedRowIndex]?.policy_id}?`}
          confirmText="Delete"
          isOpen={openDeleteConfirmDialog}
          onCancel={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
          isDisableConfirm={isDeleteInProgress}
        />

        <DialogBox
          width="auto"
          title="Contra Allocation Confirmation"
          //body={<CustomContraBody />}
          child={CustomContraBody}
          confirmText="Create"
          isOpen={openContraAllocationConfirmDialog}
          onCancel={() => {
            setContraReferencePolicies([]);
            setContraComment("");
            setOpenContraAllocationConfirmDialog(false);
            setContraReferencePoliciesInputsValues({});
            setContraReferencePolicies([]);
          }}
          onConfirm={createContraAllocation}
        />

        {/* Auto Initiation Of Corective Transfer */}
        {triggerCTWorkflow && (
          <CorrectiveTransfer
            allocation={allocationList[selectedRowIndex]}
            handleClose={() => {
              // Logic to close the dialog when the handleClose function is triggered
              setTriggerCTWorkflow(false); // Or any method to control dialog visibility
            }}
            transaction={transactionDetails}
            currencies={[]}
            isAutoInitiation={true}
            isAuthorizedUser={isAuthorizedUser}
          />
        )}

        {/* Auto Initiation Of Correction Types */}
        {triggerCorrectionTypesWorkflow && (
          <CorrectionTypes
            allocation={allocationList[selectedRowIndex]}
            handleClose={() => {
              setTriggerCorrectionTypesWorkflow(false);
            }}
            transaction={transactionDetails}
            currencies={[]}
            isAutoInitiation={true}
            isAuthorizedUser={isAuthorizedUser}
          />
        )}
      </div>
      {/* Add the Dialog component for the policy error modal */}
      <Dialog
        open={isPolicyErrorModalOpen}
        onClose={handleClosePolicyErrorModal}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Warning!"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Policy cannot be updated as there are activities associated to it.
          </DialogContentText>
          <DialogContentText id="alert-dialog-description" sx={{ mt: 2 }}>
            Kindly check !!
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClosePolicyErrorModal}
            color="primary"
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <CashAllocationEdit
        loadData={loadData}
        policy={allocationList?.[selectedRowIndex]}
        isOpen={cashAllocationDetailsEditOpen}
        onCancel={() => setCashAllocationDetailsEditOpen(false)}
      />
      <WriteOffConfirmation
        open={writeOffConfirmationOpen}
        onClose={handleWriteOffClose}
        varianceAmount={transactionDetails?.Receivable_Amount - totalAllocatedAmount}
        onAdjustAmount={handleAdjustAmount}
        onAddAllocation={handleAddAllocation}
      />
      <CashAllocationEdit loadData={loadData} policy={allocationList?.[selectedRowIndex]} isOpen={cashAllocationDetailsEditOpen} onCancel={() => setCashAllocationDetailsEditOpen(false)} />
      
      {/* Write-off auto-trigger component */}
      {/* {pendingWriteOffAllocation && (
        <WriteOffAutoTrigger allocation={pendingWriteOffAllocation} />
      )} */}
    </>
  );
}

// const TriggerCorrectiveTransferWorkflow = ({ currentAllocation, transactionDetails, loadData }) => {
//   const { formData, setFormData, submit } = useCorrectiveTrf(currentAllocation, transactionDetails);

//   const triggerCTWorkflowAPI = async () => {
//     setFormData({
//       ...formData,
//       bank_curr: currentAllocation?.policy_fk?.Settlement_Ccy ?? null,
//     })
//     await submit();
//     loadData();
//   }

//   useEffect(() => {
//     triggerCTWorkflowAPI();
//   }, []);

//   return (
//     <>
//     </>
//   )
// };
